import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Wallet,
  Users,
  Search,
  Plus,
  Eye,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ExternalLink,
  Activity,
  DollarSign,
  Power,
  PowerOff,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { walletAPI, usersAPI } from "../../services/api";
import {
  formatCurrency,
  formatDate,
  formatCompactCurrency,
} from "../../services/api";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { createResponseSelector } from "../../utils/apiHelpers";

const WalletManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterNetwork, setFilterNetwork] = useState("all");
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showWalletDetails, setShowWalletDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const queryClient = useQueryClient();

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Fetch wallets
  const { data: walletsData, isPending: walletsLoading } = useQuery({
    queryKey: [
      "wallets",
      debouncedSearchTerm,
      filterStatus,
      filterNetwork,
      currentPage,
      itemsPerPage,
    ],
    queryFn: () =>
      walletAPI.getWallets({
        search: debouncedSearchTerm || undefined,
        status: filterStatus === "all" ? undefined : filterStatus,
        network: filterNetwork === "all" ? undefined : filterNetwork,
        page: currentPage,
        limit: itemsPerPage,
      }),
    select: (data) => data.data || { wallets: [], pagination: {} },
  });

  const wallets = walletsData?.wallets || [];
  const pagination = walletsData?.pagination || {};

  // Calculate pagination info with fallbacks
  const totalWallets = pagination.total || wallets.length;
  const totalPages = pagination.pages || Math.ceil(totalWallets / itemsPerPage);
  const currentPageNum = pagination.page || currentPage;

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filterStatus, filterNetwork]);

  // Fetch wallet statistics
  const { data: walletStats } = useQuery({
    queryKey: ["wallet-stats"],
    queryFn: () => walletAPI.getWalletStats(),
    select: createResponseSelector("stats", null),
  });

  // Fetch users without wallets
  const { data: usersWithoutWallets = [] } = useQuery({
    queryKey: ["users-without-wallets"],
    queryFn: () => walletAPI.getUsersWithoutWallets(),
    select: (data) => data.data.users || [],
  });

  // Create wallet mutation
  const createWalletMutation = useMutation({
    mutationFn: (walletData) => walletAPI.createWallet(walletData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wallets"] });
      queryClient.invalidateQueries({ queryKey: ["wallet-stats"] });
      queryClient.invalidateQueries({ queryKey: ["users-without-wallets"] });
      setShowCreateModal(false);
    },
  });

  // Toggle wallet status mutation
  const toggleWalletStatusMutation = useMutation({
    mutationFn: ({ walletId, newStatus }) =>
      walletAPI.updateWallet(walletId, { status: newStatus }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["wallets"] });
      queryClient.invalidateQueries({ queryKey: ["wallet-stats"] });
      // You can add a toast notification here if you have a toast system
      console.log(
        `Wallet ${variables.walletId} status updated to ${variables.newStatus}`
      );
    },
    onError: (error) => {
      console.error("Failed to update wallet status:", error);
      // You can add error toast notification here
    },
  });

  // Page access is controlled by route permissions - no action-level check needed
  if (false) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold text-gray-900">
            Access Denied
          </h1>
          <p className="text-gray-600">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: {
        color: "bg-green-100 text-green-800",
        icon: CheckCircle,
        label: "Active",
      },
      inactive: {
        color: "bg-red-100 text-red-800",
        icon: XCircle,
        label: "Inactive",
      },
      suspended: {
        color: "bg-yellow-100 text-yellow-800",
        icon: AlertTriangle,
        label: "Suspended",
      },
      frozen: {
        color: "bg-red-100 text-red-800",
        icon: XCircle,
        label: "Frozen",
      },
    };

    const config = statusConfig[status] || statusConfig.active;
    const IconComponent = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
      >
        <IconComponent className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const getNetworkBadge = (network) => {
    const networkColors = {
      "ethereum-sepolia": "bg-blue-100 text-blue-800",
      "aeris-devnet": "bg-purple-100 text-purple-800",
    };

    const networkLabels = {
      "ethereum-sepolia": "Ethereum Sepolia",
      "aeris-devnet": "Aeris DevNet",
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          networkColors[network] || networkColors["ethereum-sepolia"]
        }`}
      >
        {networkLabels[network] || network}
      </span>
    );
  };

  const getUserDisplayName = (user) => {
    if (!user) {
      return "Unknown User";
    }

    if (user.personalInfo?.firstName && user.personalInfo?.lastName) {
      return `${user.personalInfo.firstName} ${user.personalInfo.lastName}`;
    }
    if (user.personalInfo?.fullName) {
      return user.personalInfo.fullName;
    }
    if (user.corporateInfo?.companyName) {
      return user.corporateInfo.companyName;
    }
    if (user.corporateInfo?.legalEntityName) {
      return user.corporateInfo.legalEntityName;
    }
    return user.email || "Unknown User";
  };

  const getUserRoleBadge = (role) => {
    const roleConfig = {
      investor: { label: "Investor", color: "text-blue-700 bg-blue-100" },
      custodian: { label: "Custodian", color: "text-green-700 bg-green-100" },
      issuer: { label: "Issuer", color: "text-purple-700 bg-purple-100" },
      facility_agent: {
        label: "Facility Agent",
        color: "text-orange-700 bg-orange-100",
      },
      super_admin: { label: "Super Admin", color: "text-red-700 bg-red-100" },
    };

    const config = roleConfig[role] || {
      label: role || "Unknown",
      color: "text-gray-700 bg-gray-100",
    };
    return config;
  };

  const handleViewWallet = (wallet) => {
    setSelectedWallet(wallet);
    setShowWalletDetails(true);
  };

  const handleToggleWalletStatus = (wallet) => {
    const newStatus = wallet.status === "active" ? "suspended" : "active";
    toggleWalletStatusMutation.mutate({
      walletId: wallet._id,
      newStatus,
    });
  };

  if (walletsLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div >
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Wallet Management
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Manage stakeholders' wallets and monitor wallet activity across
                the platform
              </p>
            </div>
            {/* Temporarily hidden - Create Wallet functionality not needed for now
            {true && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Wallet
              </button>
            )}
            */}
          </div>
        </div>
      </div>

      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Statistics Cards */}
        {walletStats && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-5 mb-8">
            <div className="p-6 bg-white border border-gray-200 rounded-lg">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Wallet className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Total Wallets
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {walletStats.totalWallets}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 bg-white border border-gray-200 rounded-lg">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Active Wallets
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {walletStats.activeWallets}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 bg-white border border-gray-200 rounded-lg">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Suspended</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {walletStats.suspendedWallets}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 bg-white border border-gray-200 rounded-lg">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Users className="w-6 h-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Without Wallets
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {walletStats.usersWithoutWallets}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 bg-white border border-gray-200 rounded-lg">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <DollarSign className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Total Value
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatCompactCurrency(walletStats.totalValue)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
          {/* Search Card - 60% width (3 out of 5 columns) */}
          <div className="card lg:col-span-3">
            <div className="card-body">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-gray w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search wallets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>
          </div>

          {/* Status Filter Card - 20% width (1 out of 5 columns) */}
          <div className="card lg:col-span-1">
            <div className="card-body">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="form-input"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
                <option value="frozen">Frozen</option>
              </select>
            </div>
          </div>

          {/* Network Filter Card - 20% width (1 out of 5 columns) */}
          <div className="card lg:col-span-1">
            <div className="card-body">
              <select
                value={filterNetwork}
                onChange={(e) => setFilterNetwork(e.target.value)}
                className="form-input"
              >
                <option value="all">All Networks</option>
                <option value="ethereum-sepolia">Ethereum Sepolia</option>
                <option value="aeris-devnet">Aeris DevNet</option>
              </select>
            </div>
          </div>
        </div>

        {/* Wallets Table */}
        <div className="overflow-hidden bg-white border border-gray-200 rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Investor Wallets
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  Showing {(currentPageNum - 1) * itemsPerPage + 1}-
                  {Math.min(currentPageNum * itemsPerPage, totalWallets)} of{" "}
                  {totalWallets} wallets
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Rows per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-8 py-1 text-sm border border-gray-300 rounded"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">
                    Investor
                  </th>
                  <th className="table-header-cell">
                    Wallet Address
                  </th>
                  <th className="table-header-cell">
                    Network
                  </th>
                  <th className="table-header-cell">
                    Status
                  </th>
                  <th className="table-header-cell">
                    Holdings
                  </th>
                  <th className="table-header-cell">
                    Total Value
                  </th>
                  <th className="table-header-cell">
                    Created
                  </th>
                  <th className="table-header-cell">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {wallets.map((wallet) => (
                  <tr key={wallet._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 w-10 h-10">
                          <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
                            <span className="text-sm font-medium text-blue-600">
                              {getUserDisplayName(wallet.user)
                                .charAt(0)
                                .toUpperCase() || "?"}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center space-x-2">
                            <div className="text-sm font-medium text-gray-900">
                              {getUserDisplayName(wallet.user)}
                            </div>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                getUserRoleBadge(wallet.user?.role || "unknown")
                                  .color
                              }`}
                            >
                              {
                                getUserRoleBadge(wallet.user?.role || "unknown")
                                  .label
                              }
                            </span>
                          </div>
                          <div className="text-sm text-gray-500">
                            {wallet.user?.email || "Unknown"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-mono text-sm text-gray-900">
                        {wallet.walletAddress.slice(0, 8)}...
                        {wallet.walletAddress.slice(-6)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {wallet.walletType}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getNetworkBadge(wallet.network)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(wallet.status)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {wallet.tokenHoldings || 0} tokens
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {formatCurrency(wallet.totalValue || 0)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(wallet.createdAt)}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewWallet(wallet)}
                          className="p-2 text-primary-400 rounded-md hover:text-primary-600 hover:bg-blue-50"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleWalletStatus(wallet)}
                          disabled={toggleWalletStatusMutation.isPending}
                          className={`p-2 rounded-md transition-colors ${
                            wallet.status === "active"
                              ? "text-primary-400 hover:text-red-600 hover:bg-red-50"
                              : "text-primary-400 hover:text-green-600 hover:bg-green-50"
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                          title={
                            wallet.status === "active"
                              ? "Suspend Wallet"
                              : "Activate Wallet"
                          }
                        >
                          {wallet.status === "active" ? (
                            <PowerOff className="w-4 h-4" />
                          ) : (
                            <Power className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {wallets.length === 0 && (
              <div className="py-12 text-center">
                <Wallet className="w-12 h-12 mx-auto text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No wallets found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Get started by creating a wallet for an approved investor.
                </p>
                {true && (
                  <div className="mt-6">
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create First Wallet
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {(totalPages > 1 ||
            totalWallets > itemsPerPage ||
            wallets.length > itemsPerPage) && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Page {currentPageNum} of {totalPages}
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Previous
                  </button>

                  <div className="flex items-center space-x-2">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNumber =
                        currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                      if (pageNumber > totalPages) return null;

                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`px-3 py-2 text-sm font-medium rounded-md min-w-[40px] ${
                            currentPage === pageNumber
                              ? "bg-blue-600 text-white"
                              : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Create Wallet Modal */}
        {showCreateModal && (
          <CreateWalletModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            usersWithoutWallets={usersWithoutWallets}
            onCreateWallet={createWalletMutation.mutate}
            isLoading={createWalletMutation.isPending}
          />
        )}

        {/* Wallet Details Modal */}
        {showWalletDetails && selectedWallet && (
          <WalletDetailsModal
            wallet={selectedWallet}
            isOpen={showWalletDetails}
            onClose={() => setShowWalletDetails(false)}
          />
        )}
      </div>
    </div>
  );
};

// Create Wallet Modal Component
const CreateWalletModal = ({
  isOpen,
  onClose,
  usersWithoutWallets,
  onCreateWallet,
  isLoading,
}) => {
  const [selectedUser, setSelectedUser] = useState("");
  const [walletType, setWalletType] = useState("custodial");
  const [network, setNetwork] = useState("ethereum-sepolia");
  const [securityLevel, setSecurityLevel] = useState("basic");
  const [riskProfile, setRiskProfile] = useState("medium");
  const [notes, setNotes] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    onCreateWallet({
      userId: selectedUser,
      walletType,
      network,
      securityLevel,
      riskProfile,
      notes: notes || undefined,
    });
  };

  const getUserDisplayName = (user) => {
    if (!user) {
      return "Unknown User";
    }

    let name = user.email || "Unknown User";

    if (user.personalInfo?.firstName && user.personalInfo?.lastName) {
      name = `${user.personalInfo.firstName} ${user.personalInfo.lastName}`;
    } else if (user.personalInfo?.fullName) {
      name = user.personalInfo.fullName;
    } else if (user.corporateInfo?.companyName) {
      name = user.corporateInfo.companyName;
    } else if (user.corporateInfo?.legalEntityName) {
      name = user.corporateInfo.legalEntityName;
    }

    return name;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Create New Wallet
            </h3>
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-gray-100"
            >
              <XCircle className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          <p className="mt-1 text-sm text-gray-600">
            Create a new wallet for an approved investor or custodian without an
            existing wallet.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Select User
            </label>
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Choose a user...</option>
              {usersWithoutWallets.map((user) => (
                <option key={user._id} value={user._id}>
                  {getUserDisplayName(user)} ({user.role.toUpperCase()}) -{" "}
                  {user.email}
                </option>
              ))}
            </select>
            {usersWithoutWallets.length === 0 && (
              <p className="mt-2 text-sm text-gray-500">
                All approved users already have wallets.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Wallet Type
              </label>
              <select
                value={walletType}
                onChange={(e) => setWalletType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="custodial">Custodial</option>
                <option value="non-custodial">Non-Custodial</option>
              </select>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Network
              </label>
              <select
                value={network}
                onChange={(e) => setNetwork(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="ethereum-sepolia">Ethereum Sepolia</option>
                <option value="aeris-devnet">Aeris DevNet</option>
              </select>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Security Level
              </label>
              <select
                value={securityLevel}
                onChange={(e) => setSecurityLevel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="basic">Basic</option>
                <option value="enhanced">Enhanced</option>
                <option value="premium">Premium</option>
              </select>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Risk Profile
              </label>
              <select
                value={riskProfile}
                onChange={(e) => setRiskProfile(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Add any additional notes..."
            />
          </div>

          <div className="flex justify-end pt-4 space-x-3 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedUser || isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin inline-block w-4 h-4 border-[3px] border-current border-t-transparent text-white rounded-full mr-2" />
                  Creating...
                </>
              ) : (
                "Create Wallet"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Wallet Details Modal Component
const WalletDetailsModal = ({ wallet, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Wallet Details
            </h3>
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-gray-100"
            >
              <XCircle className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        <div className="px-6 py-4">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <h4 className="mb-4 font-medium text-gray-900 text-md">
                Wallet Information
              </h4>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Wallet Address
                  </dt>
                  <dd className="font-mono text-sm text-gray-900">
                    {wallet.walletAddress}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Network</dt>
                  <dd className="text-sm text-gray-900">{wallet.network}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="text-sm text-gray-900">{wallet.status}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Wallet Type
                  </dt>
                  <dd className="text-sm text-gray-900">{wallet.walletType}</dd>
                </div>
              </dl>
            </div>

            <div>
              <h4 className="mb-4 font-medium text-gray-900 text-md">
                Owner Information
              </h4>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Name</dt>
                  <dd className="text-sm text-gray-900">
                    {wallet.user
                      ? wallet.user.personalInfo?.firstName &&
                        wallet.user.personalInfo?.lastName
                        ? `${wallet.user.personalInfo.firstName} ${wallet.user.personalInfo.lastName}`
                        : wallet.user.personalInfo?.fullName ||
                          wallet.user.corporateInfo?.companyName ||
                          wallet.user.corporateInfo?.legalEntityName ||
                          "Unknown"
                      : "Unknown User"}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="text-sm text-gray-900">
                    {wallet.user?.email || "Unknown"}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Account Type
                  </dt>
                  <dd className="text-sm text-gray-900">
                    {wallet.user?.accountType || "Unknown"}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletManagement;
